import { FirebaseClient } from '@/api/firebaseClient';
import { QUERY_KEYS } from '@/api/queries';
import { PdfIcon } from '@/components/PdfIcon';
import { SpeechExcerpt } from '@/components/Speech/SpeechExcerpt';
import { WordIcon } from '@/components/WordIcon';
import { Button, toast } from '@packages/ui';
import { useQuery } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

const fbClient = new FirebaseClient();

export const SpeechDetails = () => {
  const { speechId } = useParams<{ speechId: string }>();

  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEYS.SPEECH, speechId],
    queryFn: () => fbClient.getSpeech({ id: speechId! }),
    enabled: !!speechId,
  });

  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  if (isFetching) {
    return <div>{t('loading')}...</div>;
  }

  if (!data) {
    return <div>{t('noData')}</div>;
  }

  if (!data.data.hasPaid) {
    return <SpeechExcerpt excerpt={data.data.speech} speechId={data.data.id} />;
  }

  const copySpeech = () => {
    navigator.clipboard.writeText(data.data.speech);
    toast({
      variant: 'default',
      title: t('speechCopied'),
      description: t('copiedDescription'),
    });
  };

  return (
    <div className="w-full h-full grid place-items-center">
      <h1 className="text-2xl font-bold mb-1 mt-8">{t('speechDetails.headline')}</h1>
      <p className="mb-2 text-foreground/50">{t('speechDetails.subheader')}</p>

      <section className="max-w-[600px] mt-8">
        <ul className="flex sm:flex-row flex-col gap-2 w-full mb-1">
          <li>
            <Button size={'sm'} variant={'outline'} onClick={() => copySpeech()}>
              <Copy className="mr-2" /> {t('copySpeech')}
            </Button>
          </li>
          <li>
            <Button size={'sm'} variant={'outline'} onClick={() => copySpeech()}>
              <WordIcon className="h-6 mr-2" /> {t('cta.downloadAsWord')}
            </Button>
          </li>
          <li>
            <Button size={'sm'} variant={'outline'} onClick={() => copySpeech()}>
              <PdfIcon className="h-6 mr-2" /> {t('cta.downloadAsPdf')}
            </Button>
          </li>
        </ul>
        <div className="whitespace-pre-wrap bg-background p-6 rounded-md">{data.data.speech}</div>
      </section>
      <p className="text-foreground/50 mt-4 mb-2">{t('speechDetails.thankYou')}</p>
      <p className="text-foreground/50 text-sm">{t('speechDetails.refundNote')} </p>
      <a href="mailto:support@magic-speech.com" className="text-primary text-sm">
        support@magic-speech.com
      </a>
    </div>
  );
};
